"use strict";
/**
 * useStudentPlanEditor - Shared logic for editing a student degree plan.
 *
 * Used by both StudentPlanEditPage (full page with nav) and
 * StudentPlanIframePage (embedded Salesforce iframe, no nav).
 *
 * Each consumer provides:
 *  - studentProgramId   the ID to load
 *  - canLoad            whether auth/params are ready to trigger a load
 *  - onLoadError        called when load fails (edit page: showError toast; iframe: uses loadError state)
 *  - onDeleteSuccess    called after successful delete (edit page: navigate away; iframe: omit)
 *  - onSaveSuccess      called after successful save (optional)
 *
 * The hook uses the "latest-ref" pattern for optional callbacks so they never
 * appear in handler dependency arrays.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useStudentPlanEditor = useStudentPlanEditor;
const react_1 = require("react");
const ToastProvider_1 = require("../providers/ToastProvider");
const course_1 = require("../models/course");
const degreePlanApi_1 = require("../helpers/degreePlanApi");
const degreePlanPrint_1 = require("../helpers/degreePlanPrint");
const termApi_1 = require("../helpers/termApi");
const courseApi_1 = require("../helpers/courseApi");
const cohortApi_1 = require("../helpers/cohortApi");
const organizationApi_1 = require("../helpers/organizationApi");
const studentProgramApi_1 = require("../helpers/studentProgramApi");
const offSequenceNotes_1 = require("../helpers/offSequenceNotes");
/**
 * Returns any Course that appears non-failed in more than one term.
 * Special courses (LOA, Elective, Inactive, Repeatable) are exempt — they may
 * legitimately appear across multiple terms.
 */
function findDuplicateNonFailedCourses(termPlans) {
    const seen = new Map();
    const duplicates = new Map();
    for (const tp of termPlans) {
        for (const cp of tp.courses) {
            if (cp.failed)
                continue;
            if (course_1.CourseHelpers.isSpecial(cp.course))
                continue;
            if (seen.has(cp.course.id)) {
                duplicates.set(cp.course.id, cp.course);
            }
            else {
                seen.set(cp.course.id, cp.course);
            }
        }
    }
    return Array.from(duplicates.values());
}
/** Returns any TermPlanItem that has no courses assigned. */
function findEmptyTerms(termPlans) {
    return termPlans.filter(tp => tp.courses.length === 0);
}
/**
 * Collects all pre-save validation errors across all rules.
 * Returning all errors at once lets the user see and fix everything in one round-trip.
 */
function collectSaveValidationErrors(termPlans) {
    const errors = [];
    const emptyTerms = findEmptyTerms(termPlans);
    if (emptyTerms.length > 0) {
        const names = emptyTerms.map(tp => `"${tp.term.name}"`).join(', ');
        errors.push(emptyTerms.length === 1
            ? `${names} has no courses assigned. Please add at least one course or remove the term to save the degree plan.`
            : `The following terms have no courses assigned: ${names}. Please add courses to them or remove them to save the degree plan.`);
    }
    const duplicates = findDuplicateNonFailedCourses(termPlans);
    if (duplicates.length > 0) {
        const labels = duplicates.map(c => `"${course_1.CourseHelpers.getDisplayLabel(c)}"`);
        errors.push(duplicates.length === 1
            ? `${labels[0]} is in multiple terms and is not marked as failed in more than one. Please mark additional instances as failed or remove them to save the degree plan.`
            : `The following courses are in multiple terms and are not marked as failed in more than one: ${labels.join(', ')}. Please mark additional instances as failed or remove them to save the degree plan.`);
    }
    return errors;
}
function useStudentPlanEditor({ studentProgramId, canLoad, onLoadError, onDeleteSuccess, onSaveSuccess, }) {
    const { success, error: showError } = (0, ToastProvider_1.useToast)();
    // Latest-ref pattern: keep callbacks current without putting them in handler deps
    const onLoadErrorRef = (0, react_1.useRef)(onLoadError);
    const onDeleteSuccessRef = (0, react_1.useRef)(onDeleteSuccess);
    const onSaveSuccessRef = (0, react_1.useRef)(onSaveSuccess);
    (0, react_1.useEffect)(() => { onLoadErrorRef.current = onLoadError; }, [onLoadError]);
    (0, react_1.useEffect)(() => { onDeleteSuccessRef.current = onDeleteSuccess; }, [onDeleteSuccess]);
    (0, react_1.useEffect)(() => { onSaveSuccessRef.current = onSaveSuccess; }, [onSaveSuccess]);
    // Core state
    const [degreePlan, setDegreePlan] = (0, react_1.useState)(null);
    const [availableTerms, setAvailableTerms] = (0, react_1.useState)([]);
    const [availableCourses, setAvailableCourses] = (0, react_1.useState)([]);
    const [termPlans, setTermPlans] = (0, react_1.useState)([]);
    const [graduationDate, setGraduationDate] = (0, react_1.useState)('');
    const [offSequence, setOffSequence] = (0, react_1.useState)(false);
    const [initialSnapshot, setInitialSnapshot] = (0, react_1.useState)(null);
    // Loading / action states
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [saving, setSaving] = (0, react_1.useState)(false);
    const [exporting, setExporting] = (0, react_1.useState)(false);
    const [deleting, setDeleting] = (0, react_1.useState)(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = (0, react_1.useState)(false);
    const [loadError, setLoadError] = (0, react_1.useState)(null);
    const [saveValidationErrors, setSaveValidationErrors] = (0, react_1.useState)([]);
    // Clear pre-save validation errors as soon as the user changes the term plans
    (0, react_1.useEffect)(() => {
        setSaveValidationErrors([]);
    }, [termPlans]);
    // Load data
    (0, react_1.useEffect)(() => {
        var _a;
        if (!canLoad)
            return;
        if (!studentProgramId) {
            setLoading(false);
            const msg = 'Missing required parameter: studentProgramId';
            setLoadError(msg);
            (_a = onLoadErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onLoadErrorRef, msg);
            return;
        }
        let cancelled = false;
        const loadData = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            setLoading(true);
            setLoadError(null);
            try {
                const plans = yield degreePlanApi_1.degreePlanApi.getDegreePlanByStudentProgramId(studentProgramId);
                if (cancelled)
                    return;
                if (!plans || plans.length === 0) {
                    const msg = 'No degree plan found for this student program';
                    setLoadError(msg);
                    (_a = onLoadErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onLoadErrorRef, msg);
                    setLoading(false);
                    return;
                }
                const plan = plans[0];
                if (cancelled)
                    return;
                const offSeq = plan.offSequence || false;
                const gradDate = plan.graduationDate
                    ? new Date(plan.graduationDate).toISOString().split('T')[0]
                    : '';
                setOffSequence(offSeq);
                setGraduationDate(gradDate);
                // Fetch full cohort and full org in parallel with terms/courses.
                // The degree plan API returns only stub objects for cohort and
                // organization (id/name/tenantId only); the full records are needed
                // for program/specialization (cohort) and reference links (org).
                const [terms, courses, fullCohort, allOrgs] = yield Promise.all([
                    termApi_1.termApi.getTermsByOrganization(plan.organization.id),
                    courseApi_1.courseApi.getCoursesByOrganization(plan.organization.id),
                    ((_b = plan.cohort) === null || _b === void 0 ? void 0 : _b.id)
                        ? cohortApi_1.cohortApi.getCohortById(plan.cohort.id).catch(() => null)
                        : Promise.resolve(null),
                    ((_c = plan.organization) === null || _c === void 0 ? void 0 : _c.id)
                        ? organizationApi_1.organizationApi.getOrganizations().catch(() => null)
                        : Promise.resolve(null),
                ]);
                if (cancelled)
                    return;
                // Merge full cohort data (program, specialization, track) onto the plan.
                if (fullCohort) {
                    plan.cohort = fullCohort;
                }
                // Merge full org data (academicCalendarLink, courseDescriptionsLink) onto
                // the plan.  The org list is small and already fetched; find by id.
                if (allOrgs) {
                    const fullOrg = allOrgs.find(o => o.id === plan.organization.id);
                    if (fullOrg)
                        plan.organization = fullOrg;
                }
                const termsMap = new Map(terms.map(t => [t.id, t]));
                const coursesMap = new Map(courses.map(c => [c.id, c]));
                setDegreePlan(plan);
                const planItems = plan.terms.map((term) => ({
                    id: term.id,
                    term: termsMap.get(term.term.id) || term.term,
                    position: term.position,
                    courses: term.courses.map((cp) => ({
                        id: `${term.id}-${cp.course.id}`,
                        course: coursesMap.get(cp.course.id) || cp.course,
                        failed: cp.failed,
                    })),
                }));
                setTermPlans(planItems);
                setAvailableTerms(terms);
                setAvailableCourses(courses);
                setInitialSnapshot({ termPlans: planItems, graduationDate: gradDate, offSequence: offSeq });
            }
            catch (err) {
                if (!cancelled) {
                    const msg = err instanceof Error ? err.message : 'Failed to load degree plan';
                    setLoadError(msg);
                    (_d = onLoadErrorRef.current) === null || _d === void 0 ? void 0 : _d.call(onLoadErrorRef, msg);
                }
            }
            finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        });
        loadData();
        return () => {
            cancelled = true;
        };
    }, [studentProgramId, canLoad]);
    // Dirty check: true when state differs from the last saved/loaded snapshot
    const isDirty = initialSnapshot !== null &&
        (JSON.stringify(termPlans) !== JSON.stringify(initialSnapshot.termPlans) ||
            graduationDate !== initialSnapshot.graduationDate ||
            offSequence !== initialSnapshot.offSequence);
    const handleToggleFailed = (0, react_1.useCallback)((termId, courseId) => {
        setTermPlans((prev) => prev.map((tp) => {
            if (tp.id !== termId)
                return tp;
            return Object.assign(Object.assign({}, tp), { courses: tp.courses.map((cp) => cp.id === courseId ? Object.assign(Object.assign({}, cp), { failed: !cp.failed }) : cp) });
        }));
    }, []);
    /**
     * Push off-sequence, notes, and final-term-active to Salesforce after a
     * successful degree plan save.  Failures are logged but do not block the
     * overall save from succeeding (matching legacy Angular behaviour).
     */
    const syncCrmAfterSave = (0, react_1.useCallback)((savedPlan, spId) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const resolvedSpId = spId || savedPlan.studentProgramId;
        if (!resolvedSpId || !((_a = savedPlan.profile) === null || _a === void 0 ? void 0 : _a.id) || !((_b = savedPlan.academicProgram) === null || _b === void 0 ? void 0 : _b.id))
            return;
        try {
            const notes = (0, offSequenceNotes_1.buildOffSequenceNotes)({
                planTerms: savedPlan.terms,
                availableTerms,
                availableCourses,
            });
            const finalTermActive = (0, offSequenceNotes_1.getFinalActiveTermSfName)(savedPlan.finalActiveTermId, availableTerms);
            yield studentProgramApi_1.studentProgramApi.updateStudentProgramCrm({
                studentProgramId: resolvedSpId,
                academicProgramId: savedPlan.academicProgram.id,
                profileId: savedPlan.profile.id,
                tenantId: savedPlan.tenantId,
                offSequence: (_c = savedPlan.offSequence) !== null && _c !== void 0 ? _c : false,
                offSequenceNotes: notes,
                finalTermActive,
            });
        }
        catch (err) {
            console.error('CRM sync after save failed:', err);
        }
    }), [availableTerms, availableCourses]);
    /**
     * Clear off-sequence / final-term-active in Salesforce after a plan is
     * deleted, matching the legacy Angular notifyOffSequenceOnDegreePlanDelete().
     */
    const syncCrmAfterDelete = (0, react_1.useCallback)((deletedPlan) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const spId = deletedPlan.studentProgramId || studentProgramId || undefined;
        if (!spId || !((_a = deletedPlan.profile) === null || _a === void 0 ? void 0 : _a.id) || !((_b = deletedPlan.academicProgram) === null || _b === void 0 ? void 0 : _b.id))
            return;
        try {
            yield studentProgramApi_1.studentProgramApi.updateStudentProgramCrm({
                studentProgramId: spId,
                academicProgramId: deletedPlan.academicProgram.id,
                profileId: deletedPlan.profile.id,
                tenantId: deletedPlan.tenantId,
                offSequence: false,
                offSequenceNotes: '',
                finalTermActive: '',
            });
        }
        catch (err) {
            console.error('CRM sync after delete failed:', err);
        }
    }), [studentProgramId]);
    const handleSave = (0, react_1.useCallback)(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!degreePlan)
            return;
        const validationErrors = collectSaveValidationErrors(termPlans);
        if (validationErrors.length > 0) {
            setSaveValidationErrors(validationErrors);
            return;
        }
        setSaveValidationErrors([]);
        setSaving(true);
        try {
            const spId = degreePlan.studentProgramId || studentProgramId || undefined;
            const updatedPlan = yield degreePlanApi_1.degreePlanApi.updateStudentPlan({
                id: degreePlan.id,
                type: 'student',
                tenantId: degreePlan.tenantId,
                cohort: degreePlan.cohort,
                organization: degreePlan.organization,
                studentProgramId: spId,
                terms: termPlans.map((tp) => ({
                    id: tp.id,
                    term: tp.term,
                    position: tp.position,
                    courses: tp.courses.map((cp) => ({
                        course: cp.course,
                        failed: cp.failed || false,
                    })),
                })),
                graduationDate: graduationDate ? new Date(graduationDate) : undefined,
            });
            // Sync state from the server response so values like offSequence reflect
            // whatever the backend persisted, without requiring a page refresh.
            const savedOffSequence = (_a = updatedPlan.offSequence) !== null && _a !== void 0 ? _a : offSequence;
            const savedGraduationDate = updatedPlan.graduationDate
                ? new Date(updatedPlan.graduationDate).toISOString().split('T')[0]
                : graduationDate;
            setOffSequence(savedOffSequence);
            setGraduationDate(savedGraduationDate);
            setDegreePlan(updatedPlan);
            // Push off-sequence / final-term-active to Salesforce via esil-crm,
            // mirroring the legacy Angular notifyOffSequence() flow.
            yield syncCrmAfterSave(updatedPlan, spId);
            success('Degree plan updated successfully');
            setInitialSnapshot({ termPlans, graduationDate: savedGraduationDate, offSequence: savedOffSequence });
            (_b = onSaveSuccessRef.current) === null || _b === void 0 ? void 0 : _b.call(onSaveSuccessRef);
        }
        catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to update degree plan');
        }
        finally {
            setSaving(false);
        }
    }), [degreePlan, termPlans, graduationDate, offSequence, studentProgramId, syncCrmAfterSave, success, showError]);
    const handleExport = (0, react_1.useCallback)(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!degreePlan)
            return;
        setExporting(true);
        try {
            // Resolve cohort starting-term display name via the legacy lookup:
            // fetch the cohort's master plan and read its position-0 term.
            // printDegreePlan uses a hidden iframe, so there is no popup-blocker
            // constraint — async work here is safe.
            let cohortStartTermName;
            if ((_a = degreePlan.cohort) === null || _a === void 0 ? void 0 : _a.id) {
                try {
                    const cohortMasterPlan = yield degreePlanApi_1.degreePlanApi.getDegreePlanByCohortId(degreePlan.cohort.id);
                    const startTerm = cohortMasterPlan.terms.find((t) => t.position === 0);
                    if (startTerm) {
                        const resolved = availableTerms.find((t) => t.id === startTerm.term.id);
                        cohortStartTermName = (resolved === null || resolved === void 0 ? void 0 : resolved.clientName) || (resolved === null || resolved === void 0 ? void 0 : resolved.name) || startTerm.term.clientName || startTerm.term.name;
                    }
                }
                catch (_b) {
                    // Swallow — fallback to degreePlan.cohort.name happens inside printDegreePlan.
                }
            }
            (0, degreePlanPrint_1.printDegreePlan)({ degreePlan, termPlans, graduationDate, cohortStartTermName });
        }
        catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to open print dialog');
        }
        finally {
            setExporting(false);
        }
    }), [degreePlan, termPlans, graduationDate, availableTerms, showError]);
    const handleDelete = (0, react_1.useCallback)(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!degreePlan)
            return;
        setDeleting(true);
        try {
            yield degreePlanApi_1.degreePlanApi.deleteStudentPlan(degreePlan.id);
            // Clear off-sequence / final-term-active in Salesforce, matching the
            // legacy Angular notifyOffSequenceOnDegreePlanDelete() flow.
            yield syncCrmAfterDelete(degreePlan);
            success('Degree plan deleted successfully');
            setShowDeleteConfirm(false);
            setDegreePlan(null);
            (_a = onDeleteSuccessRef.current) === null || _a === void 0 ? void 0 : _a.call(onDeleteSuccessRef);
        }
        catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to delete degree plan');
        }
        finally {
            setDeleting(false);
        }
    }), [degreePlan, syncCrmAfterDelete, success, showError]);
    return {
        degreePlan,
        setDegreePlan,
        availableTerms,
        availableCourses,
        termPlans,
        setTermPlans,
        graduationDate,
        setGraduationDate,
        offSequence,
        setOffSequence,
        isDirty,
        loading,
        saving,
        exporting,
        deleting,
        showDeleteConfirm,
        setShowDeleteConfirm,
        loadError,
        saveValidationErrors,
        handleToggleFailed,
        handleSave,
        handleExport,
        handleDelete,
    };
}
